class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const wasActive = this.classList.contains('active');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      if (!wasActive) this.playUpsellBadgeAnimation();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  playUpsellBadgeAnimation() {
    const badge = this.querySelector('[data-upsell-badge][data-badge-animation="pulse_once"]');
    if (!badge || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    badge.classList.remove('saux-cart-upsell__badge--pulse');
    void badge.offsetWidth;
    badge.classList.add('saux-cart-upsell__badge--pulse');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

window.SauxUpsellTracking = window.SauxUpsellTracking || (() => {
  const storageKey = 'sauxUpsellTrackingEvents';
  const memoryKeys = new Set();
  const allowedParameters = new Set([
    'product_id',
    'product_handle',
    'product_title',
    'variant_id',
    'variant_title',
    'quantity',
    'unit_price',
    'currency',
    'cart_value_before',
    'cart_value_after',
    'cart_item_count',
    'upsell_source',
    'upsell_version',
    'placement',
    'device_category',
    'error_type',
    'upsell_line_count',
  ]);

  const number = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const deviceCategory = () => {
    if (window.matchMedia('(max-width: 767px)').matches) return 'mobile';
    if (window.matchMedia('(max-width: 989px)').matches) return 'tablet';
    return 'desktop';
  };

  const analyticsAllowed = () => {
    const privacy = window.Shopify?.customerPrivacy;
    return !privacy || typeof privacy.analyticsProcessingAllowed !== 'function' || privacy.analyticsProcessingAllowed();
  };

  const sanitize = (data) =>
    Object.fromEntries(
      Object.entries(data).filter(
        ([key, value]) => allowedParameters.has(key) && value !== undefined && value !== null && value !== ''
      )
    );

  const storedKeys = () => {
    if (!analyticsAllowed()) return [];

    try {
      const stored = JSON.parse(window.sessionStorage.getItem(storageKey) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch (_error) {
      return [];
    }
  };

  const remember = (key) => {
    if (!analyticsAllowed()) return false;
    if (memoryKeys.has(key)) return false;

    const keys = storedKeys();
    if (keys.includes(key)) {
      memoryKeys.add(key);
      return false;
    }

    memoryKeys.add(key);
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify([...keys, key]));
    } catch (_error) {
      return true;
    }
    return true;
  };

  const publishEvent = (name, data) => {
    if (!analyticsAllowed() || typeof window.Shopify?.analytics?.publish !== 'function') {
      return Promise.resolve(false);
    }

    const payload = sanitize({ ...data, device_category: deviceCategory() });
    if (window.SAUX_UPSELL_TRACKING_DEBUG === true) console.debug('[SAUX upsell]', name, payload);
    return Promise.resolve(window.Shopify.analytics.publish(name, payload)).catch(() => false);
  };

  const publishOnce = (key, name, data) => {
    if (typeof window.Shopify?.analytics?.publish !== 'function') return Promise.resolve(false);
    if (!remember(key)) return Promise.resolve(false);
    return publishEvent(name, data);
  };

  const rowData = (row) => ({
    product_id: row?.dataset.sauxProductId,
    product_handle: row?.dataset.sauxProductHandle,
    product_title: row?.dataset.sauxProductTitle,
    variant_id: row?.dataset.sauxVariantId,
    variant_title: row?.dataset.sauxVariantTitle,
    quantity: number(row?.dataset.sauxQuantity),
    unit_price: number(row?.dataset.sauxUnitPrice),
    currency: row?.dataset.sauxCurrency,
    cart_value_before: number(row?.dataset.sauxCartValue),
    cart_item_count: number(row?.dataset.sauxCartItemCount),
    upsell_source: 'cart_drawer',
    upsell_version: row?.dataset.sauxUpsellVersion,
    placement: 'drawer_offer',
  });

  const checkout = () => {
    const rows = [...document.querySelectorAll('[data-saux-upsell-attributed="true"]')];
    if (!rows.length) return;

    const first = rowData(rows[0]);
    const stateKey = rows.map((row) => `${row.dataset.sauxVariantId}:${row.dataset.sauxQuantity}`).join('|');
    const quantity = rows.reduce((total, row) => total + (number(row.dataset.sauxQuantity) || 0), 0);
    publishOnce(`checkout:${stateKey}`, 'saux_checkout_started_with_upsell', {
      ...first,
      quantity,
      upsell_line_count: rows.length,
    });
  };

  document.addEventListener('submit', (event) => {
    if (event.target.id === 'CartDrawer-Form') checkout();
  });

  document.addEventListener('visitorConsentCollected', (event) => {
    if (!event.detail?.analyticsAllowed) return;
    document.querySelectorAll('cart-drawer-upsell').forEach((upsell) => {
      upsell.trackEligible?.();
      upsell.trackViewedIfVisible?.();
    });
  });

  window.addEventListener('load', () => {
    document.querySelectorAll('cart-drawer-upsell').forEach((upsell) => upsell.trackEligible?.());
  }, { once: true });

  return {
    number,
    publishEvent,
    publishOnce,
    rowData,
    removal(row, cart) {
      if (!row || row.dataset.sauxUpsellAttributed !== 'true') return;
      const data = rowData(row);
      publishEvent('saux_upsell_removed', {
        ...data,
        cart_value_after: number(cart.total_price) / 100,
        cart_item_count: number(cart.item_count),
      });
    },
  };
})();

class CartDrawerUpsell extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.saux-cart-upsell__button');
    this.status = this.querySelector('[role="status"]');
    this.price = this.querySelector('[data-upsell-price]');
    this.variantTitle = this.querySelector('[data-upsell-variant-title]');
    this.image = this.querySelector('.saux-cart-upsell__image');
    this.timer = this.querySelector('[data-upsell-timer]');
    this.countdown = this.querySelector('[data-upsell-countdown]');
    this.storageKey = `sauxCartOfferDeadline:v1:${this.dataset.productId}`;
    this.timerInterval = null;
    this.expired = false;
    this.restoreTimer();
    if (this.expired) return;
    this.variantInputs = this.querySelectorAll('input[data-variant-price]');
    this.variantInputs.forEach((input) => input.addEventListener('change', this.onVariantChange.bind(this)));
    if (this.button) this.button.addEventListener('click', this.addToCart.bind(this));
    this.trackEligible();
    this.visibilityObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
        this.startTimer();
        this.trackViewed();
      }
    }, { threshold: 0.5 });
    this.visibilityObserver.observe(this.closest('.saux-cart-upsell'));
  }

  disconnectedCallback() {
    this.visibilityObserver?.disconnect();
    window.clearInterval(this.timerInterval);
  }

  storedDeadline() {
    try {
      const deadline = Number(window.localStorage.getItem(this.storageKey));
      return Number.isFinite(deadline) && deadline > 0 ? deadline : null;
    } catch (_error) {
      return null;
    }
  }

  restoreTimer() {
    const deadline = this.storedDeadline();
    if (!deadline) return;

    this.deadline = deadline;
    if (deadline <= Date.now()) {
      this.expireOffer();
      return;
    }
    this.renderCountdown();
    this.runTimer();
  }

  startTimer() {
    if (this.expired) return;
    if (!this.deadline) {
      const configuredMinutes = Number(this.dataset.offerDurationMinutes);
      const durationMinutes = Number.isFinite(configuredMinutes) && configuredMinutes > 0 ? configuredMinutes : 10;
      this.deadline = Date.now() + durationMinutes * 60 * 1000 - 1;
      try {
        window.localStorage.setItem(this.storageKey, String(this.deadline));
      } catch (_error) {}
    }
    this.renderCountdown();
    this.runTimer();
  }

  runTimer() {
    if (this.timerInterval || this.expired) return;
    this.timerInterval = window.setInterval(() => this.renderCountdown(), 250);
  }

  renderCountdown() {
    if (!this.deadline || !this.countdown) return;
    const remaining = this.deadline - Date.now();
    if (remaining <= 0) {
      this.expireOffer();
      return;
    }

    const secondsRemaining = Math.max(0, Math.floor(remaining / 1000));
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    this.timer?.classList.toggle('is-ending', remaining <= 2 * 60 * 1000);
    this.countdown.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  expireOffer() {
    this.expired = true;
    window.clearInterval(this.timerInterval);
    this.timerInterval = null;
    if (this.countdown) this.countdown.textContent = '00:00';
    if (this.button) {
      this.button.disabled = true;
      this.button.setAttribute('aria-disabled', 'true');
    }
    this.closest('.saux-cart-upsell')?.setAttribute('hidden', '');
  }

  eventData() {
    const selectedVariant = this.querySelector('input[data-variant-price]:checked');
    return {
      product_id: this.dataset.productId,
      product_handle: this.dataset.productHandle,
      product_title: this.dataset.productTitle,
      variant_id: selectedVariant?.value || this.dataset.variantId,
      variant_title: selectedVariant?.dataset.variantTitle || this.dataset.variantTitle,
      quantity: 1,
      unit_price: window.SauxUpsellTracking.number(selectedVariant?.dataset.unitPrice || this.dataset.unitPrice),
      currency: this.dataset.currency,
      cart_value_before: window.SauxUpsellTracking.number(this.dataset.cartValue),
      cart_item_count: window.SauxUpsellTracking.number(this.dataset.cartItemCount),
      upsell_source: this.dataset.upsellSource,
      upsell_version: this.dataset.upsellVersion,
      placement: this.dataset.placement,
    };
  }

  selectedVariantId() {
    const selectedVariant = this.querySelector('input[data-variant-price]:checked');
    const variantId = String(selectedVariant?.value || this.dataset.variantId || '').trim();
    const allowedVariantIds = new Set(
      String(this.dataset.variantIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );

    if (!/^\d+$/.test(variantId) || !allowedVariantIds.has(variantId)) return null;
    return variantId;
  }

  trackingKey(eventName) {
    return `${eventName}:${this.dataset.cartStateKey}:${this.dataset.productId}`;
  }

  trackEligible() {
    window.SauxUpsellTracking.publishOnce(
      this.trackingKey('eligible'),
      'saux_upsell_eligible',
      this.eventData()
    );
  }

  trackViewed() {
    window.SauxUpsellTracking.publishOnce(this.trackingKey('viewed'), 'saux_upsell_viewed', this.eventData());
  }

  trackViewedIfVisible() {
    const card = this.closest('.saux-cart-upsell');
    const drawer = this.closest('cart-drawer');
    if (!card || !drawer?.classList.contains('active')) return;

    const bounds = card.getBoundingClientRect();
    const visibleHeight = Math.min(bounds.bottom, window.innerHeight) - Math.max(bounds.top, 0);
    if (bounds.height > 0 && visibleHeight / bounds.height >= 0.5) this.trackViewed();
  }

  onVariantChange(event) {
    if (!event.target.checked || event.target.disabled) return;

    this.price.textContent = event.target.dataset.variantPrice;
    if (this.variantTitle) this.variantTitle.textContent = event.target.dataset.variantTitle;
    if (this.image && event.target.dataset.variantImage180) {
      this.image.src = event.target.dataset.variantImage180;
      this.image.srcset = `${event.target.dataset.variantImage90} 90w, ${event.target.dataset.variantImage135} 135w, ${event.target.dataset.variantImage180} 180w`;
      this.image.alt = event.target.dataset.variantImageAlt || this.dataset.productTitle;
    }
    this.status.textContent = '';
    window.SauxUpsellTracking.publishEvent('saux_upsell_variant_selected', this.eventData());
  }

  addToCart() {
    if (this.expired || this.deadline && this.deadline <= Date.now()) {
      this.expireOffer();
      return;
    }
    if (this.button.getAttribute('aria-disabled') === 'true') return;

    const drawer = this.closest('cart-drawer');
    const variantId = this.selectedVariantId();
    const loadingMessage = this.button.dataset.loadingMessage;
    const errorMessage = this.button.dataset.errorMessage;
    const eventData = this.eventData();
    let addConfirmed = false;

    if (!variantId || !drawer) {
      this.status.textContent = errorMessage;
      window.SauxUpsellTracking.publishEvent('saux_upsell_add_failed', {
        ...eventData,
        error_type: 'invalid_variant',
      });
      return;
    }

    this.button.setAttribute('aria-disabled', 'true');
    this.button.setAttribute('aria-busy', 'true');
    this.button.disabled = true;
    this.button.classList.add('loading');
    this.querySelector('.loading__spinner')?.classList.remove('hidden');
    this.status.textContent = loadingMessage;
    window.SauxUpsellTracking.publishEvent('saux_upsell_add_clicked', eventData);

    const body = JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: {
            _saux_upsell_source: 'cart_drawer',
            _saux_upsell_version: this.dataset.upsellVersion,
            _saux_upsell_placement: 'drawer_offer',
          },
        },
      ],
      sections: drawer.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
      .then(async (response) => {
        let parsedResponse;
        try {
          parsedResponse = await response.json();
        } catch (_error) {
          const invalidResponseError = new Error(errorMessage);
          invalidResponseError.sauxType = 'invalid_response';
          throw invalidResponseError;
        }

        if (!response.ok) {
          const responseError = new Error(parsedResponse.description || errorMessage);
          responseError.sauxType = 'http_error';
          throw responseError;
        }
        return parsedResponse;
      })
      .then((response) => {
        if (response.status) {
          const rejectedError = new Error(response.description || errorMessage);
          rejectedError.sauxType = 'shopify_rejected';
          throw rejectedError;
        }

        addConfirmed = true;
        this.closest('.saux-cart-upsell')?.setAttribute('hidden', '');
        fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((cartResponse) => (cartResponse.ok ? cartResponse.json() : null))
          .catch(() => null)
          .then((cart) => {
            window.SauxUpsellTracking.publishEvent('saux_upsell_add_succeeded', {
              ...eventData,
              cart_value_after: cart ? window.SauxUpsellTracking.number(cart.total_price) / 100 : undefined,
              cart_item_count: cart ? window.SauxUpsellTracking.number(cart.item_count) : undefined,
            });
          });

        drawer.renderContents(response);
        return publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-upsell',
          productVariantId: variantId,
          cartData: response,
        });
      })
      .catch((error) => {
        if (addConfirmed) {
          console.error(error);
          return;
        }
        if (!addConfirmed) {
          const errorType = error.sauxType || (error instanceof TypeError ? 'network_error' : 'unknown_error');
          window.SauxUpsellTracking.publishEvent('saux_upsell_add_failed', { ...eventData, error_type: errorType });
        }
        this.status.textContent = error.message || errorMessage;
        this.button.removeAttribute('aria-disabled');
        this.button.removeAttribute('aria-busy');
        this.button.disabled = false;
        this.button.classList.remove('loading');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      });
  }
}

customElements.define('cart-drawer-upsell', CartDrawerUpsell);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
